import { AXIOS_INSTANCE } from '@api'
import { useAppSelector } from '@store/hooks'
import { queryClient } from '@store/provider/TanStackProvider'
import { useMutation, useQuery } from '@tanstack/react-query'
import { StaffIncluded, StaffMeta, StaffOptions, StaffSAttrProps } from '@type/staff.types'
import { GeneralResMock } from '../types/general.type'

export const useStaff = (arg?: StaffOptions) => {
  const { user } = useAppSelector(state => state.user)
  const isLoggedIn = !!user?.authable_id
  const id = arg?.id
  const page = arg?.page
  const pageCount = arg?.pageCount
  const employment_type = arg?.employment_type
  const status = arg?.status
  const query = arg?.query
  const department_id = arg?.department_id
  const exclude_roles = arg?.exclude_roles
  const role = arg?.role
  const sort_dir = arg?.sort_dir || 'desc'
  const sort_by = arg?.sort_by || 'created_at'

  const { data: staffs, isLoading: staffIsLoading } = useQuery({
    enabled: isLoggedIn && !id,
    queryFn: async () => {
      const { data } = await AXIOS_INSTANCE.get<GeneralResMock<StaffSAttrProps[], StaffIncluded[], StaffMeta>>(
        '/staffs',
        {
          params: {
            ...(sort_dir && { sort_dir }),
            ...(page && { 'page[number]': page }),
            ...(pageCount && { 'page[size]': pageCount }),
            ...(role && { role }),
            ...(sort_by && { sort_by }),
            ...(status && { status }),
            ...(department_id && { department_id }),
            ...(query && { query }),
            ...(exclude_roles && { exclude_roles }),
            ...(employment_type && { employment_type })
          }
        }
      )
      return data
    },
    queryKey: [
      'staffs',
      {
        department_id,
        employment_type,
        exclude_roles,
        page,
        pageCount,
        query,
        role,
        sort_by,
        sort_dir,
        status
      }
    ]
  })

  const { data: singleStaff, isLoading: singleStaffIsLoading } = useQuery({
    enabled: isLoggedIn && !!id,
    queryFn: async () => {
      const endpoint = `staffs/${id}`
      const { data } = await AXIOS_INSTANCE.get<GeneralResMock<StaffSAttrProps, StaffIncluded[]>>(endpoint)
      return data
    },
    queryKey: ['staffs', { id }]
  })

  const { mutateAsync: createStaff } = useMutation({
    mutationFn: async (formVal: any) => {
      const { data } = await AXIOS_INSTANCE.post('/staffs', formVal, {
        headers: { 'Content-Type': 'application/vnd.api+json' }
      })
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['staffs'] })
    }
  })

  const { mutateAsync: editStaff } = useMutation({
    mutationFn: async ({ formVal, id }: { formVal: any; id: string }) => {
      const { data } = await AXIOS_INSTANCE.patch(`/staffs/${id}`, formVal, {
        headers: { 'Content-Type': 'application/vnd.api+json' }
      })
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['staffs'] })
      queryClient.invalidateQueries({ queryKey: ['staffs', { id }] })
    }
  })

  const { mutateAsync: updateStatus } = useMutation({
    mutationFn: async ({ formVal, id }: { formVal: { status: string }; id: string }) => {
      const { data } = await AXIOS_INSTANCE.patch(`/staffs/${id}/update_status`, formVal)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['staffs'] })
      queryClient.invalidateQueries({ queryKey: ['staffs', { id }] })
    }
  })

  const { mutateAsync: deleteStaff } = useMutation({
    mutationFn: async (id: string) => {
      await AXIOS_INSTANCE.delete(`staffs/${id}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['staffs'] })
    }
  })

  const { mutateAsync: resendEmail, isPending: resendEmailIsPending } = useMutation({
    mutationFn: async (id: string) => {
      await AXIOS_INSTANCE.post(`/staffs/${id}/resend_invitation`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['staffs'] })
    }
  })

  return {
    createStaff,
    deleteStaff,
    editStaff,
    meta: staffs?.meta.staff_stats,
    singleIncluded: singleStaff?.included,
    singleStaff: singleStaff?.data,
    singleStaffIsLoading,
    staffIsLoading,
    staffPagination: staffs?.meta?.pagination,
    staffs: staffs?.data,
    staffsIncluded: staffs?.included,
    updateStatus,
    resendEmail,
    resendEmailIsPending
  }
}
